import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "../redux/hooks";
import {
  IExperience,
  ProfileVideo,
  replaceProfileVideos,
} from "../redux/features/candidateProfileSlice";
import { useProfileQuery } from "@/queries/user/profile";
import {
  Avatar,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  Dropdown,
  Option as FluentOption,
  InfoLabel,
  Skeleton,
  SkeletonItem,
} from "@fluentui/react-components";
import {
  ArrowLeftSVG,
  CalendarSVG,
  ClockSVG,
  CloseCircleSVG,
  DocumentTextBoldSVG,
  DollarCircleSVG,
  ExportMissingSVG,
  ExportSVG,
  LinkedInSVG,
  LocationSVG,
  MonitorMobileSVG,
  PeopleSVG,
  ReceiptEditSVG,
  SocialIconSVG,
  TickCircleSVG,
  UserSVG,
  UserTickSVG,
  VerifiedTickSVG,
} from "../assets/svgs";
import { CirclePlay, Eye, EyeOff } from "lucide-react";
import { QuestionRegular } from "@fluentui/react-icons";
import Reference from "../elements/Reference";
import Project from "../elements/Project";
import CandidateProfileVideoDialog from "@/components/dialogs/candidate/videos";
import { useDialogStyles } from "../fluentStyles";

import CandidateNameLocationTitleEditFormDialog from "@/components/forms/candidate/update-name-location-title";
import CandidateBiographyEditFormDialog from "@/components/forms/candidate/update-biography";
import CandidateIdealRoleEditFormDialog from "@/components/forms/candidate/update-ideal-engagement";
import CandidateCompetenciesEditFormDialog from "@/components/forms/candidate/update-core-competencies";
import CandidateFundraisingEditFormDialog from "@/components/forms/candidate/update-fundraising";
import CandidateAvailabilityEditFormDialog from "@/components/forms/candidate/update-availability";
import CandidatePricingEditFormDialog from "@/components/forms/candidate/update-pricing";
import CandidateLinksEditFormDialog from "@/components/forms/candidate/update-links";
import CandidateQuestionnaireEditFormDialog from "@/components/forms/candidate/update-questionnaire";
import CandidateExperienceFormDialog from "@/components/forms/candidate/add-update-experience";
import CandidateProjectFormDialog from "@/components/forms/candidate/add-update-project";
import CandidateReferenceFormDialog from "@/components/forms/candidate/add-delete-reference";

import "react-datepicker/dist/react-datepicker.css";
import dateStringToLocalDate from "../utils/dateStringToLocalDate";
import { useCandidateQuery } from "@/queries/candidate/candidate";

const CandidateProfileEdit = () => {
  const { candidateId } = useParams();

  const { profile, profileLoading } = useProfileQuery();
  const { candidate, candidateLoading } = useCandidateQuery(candidateId);

  const navigate = useNavigate();

  const profileId = candidateId ?? profile?.id ?? "";

  const loading = !profile || !candidate || profileLoading || candidateLoading;

  useEffect(() => {
    document.title = "Venturous Profile";
  }, []);

  useEffect(() => {
    if (
      profile?.id === profileId &&
      candidate?.applicationComplete &&
      !profile?.onboarded
    ) {
      navigate("/onboarding");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate, profile]);

  const experience = <Experience profileId={profileId} />;

  const questionnaire = <Questionnaires profileId={profileId} />;

  return (
    <div className="min-h-[calc(100vh-48px)] -mx-6 -mt-4 bg-[#f3f5f7] px-6 py-4">
      <div className="flex gap-5 mt-4 self-stretch items-stretch">
        {!loading &&
          !candidate?.applicationComplete &&
          !profile?.userType.includes("Admin") && (
            <div className="fixed top-0 left-0 w-full h-full bg-white bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-10">
              <div className="text-center">
                <p className="text-xl font-semibold">
                  Your profile will be unlocked after your application is
                  approved
                </p>
              </div>
            </div>
          )}
        <div className="flex flex-col gap-8 self-stretch items-stretch flex-1 w-full lg:max-w-[33%]">
          <NameLocationTitle profileId={profileId} />
          <div className="flex flex-col gap-5 items-stretch">
            <Biography profileId={profileId} />
            <Videos profileId={profileId} />
            <IdealEngagement profileId={profileId} />
            <CoreCompetencies profileId={profileId} />
            <Fundraising profileId={profileId} />
            <Availability profileId={profileId} />
            <Pricing profileId={profileId} />
            <Links profileId={profileId} />
            <div className="lg:hidden">{experience}</div>
            <div className="lg:hidden">{questionnaire}</div>
            <div className="lg:hidden">
              <References profileId={profileId} />
            </div>
            <div className="lg:hidden">
              <Projects profileId={profileId} />
            </div>
          </div>
        </div>
        <div className="hidden lg:flex flex-col gap-5 self-stretch items-start flex-[2_0_0%]">
          {experience}
          {questionnaire}
          <References profileId={profileId} />
          <Projects profileId={profileId} />
        </div>
      </div>
    </div>
  );
};

const NameLocationTitle = ({ profileId }: { profileId: string }) => {
  const { candidate, candidateLoading } = useCandidateQuery(profileId);

  if (candidateLoading || !candidate) {
    return (
      <Skeleton className="card flex-row gap-4 w-full">
        <div className="flex flex-col items-start rounded-full relative">
          <SkeletonItem
            shape="circle"
            style={{ height: "88px", width: "88px" }}
          />
        </div>
        <div className="flex flex-col items-start gap-1 flex-1">
          <div className="flex flex-col gap-[2px] items-start">
            <SkeletonItem style={{ height: "32px", width: "150px" }} />
            <SkeletonItem style={{ height: "20px", width: "100px" }} />
            <SkeletonItem style={{ height: "20px", width: "100px" }} />
            <SkeletonItem style={{ height: "20px", width: "100px" }} />
          </div>
          <SkeletonItem style={{ height: "24px", width: "100px" }} />
        </div>
      </Skeleton>
    );
  }

  return (
    <div className="card flex-row gap-4 w-full">
      <div className="flex flex-col items-start rounded-full relative">
        <Avatar
          image={{ src: candidate.profilePictureLink }}
          name={candidate.name}
          size={72}
          style={{ width: "88px", height: "88px" }}
        />
        <VerifiedTickSVG className="absolute bottom-0 right-0" />
      </div>
      <div className="flex flex-col items-start gap-1 flex-1">
        <div className="flex flex-col gap-[2px] items-start">
          <p className="text-2xl font-semibold tracking-[-0.24px] text-[#22234A]">
            {candidate.name}
          </p>
          <p className="text-sm font-medium text-[#727E94]">
            {candidate.titleAndEmployer}
          </p>
          {candidate.yearsOfExperience ? (
            <p className="text-sm font-medium text-[#727E94]">
              {`${candidate.yearsOfExperience} ${candidate.yearsOfExperience === 1 ? "year" : "years"} of experience${candidate.yearsOfHealthcareExperience ? `, ${candidate.yearsOfHealthcareExperience} in healthcare` : ""}`}
            </p>
          ) : null}
        </div>
        {location ? (
          <div className="flex items-center gap-1">
            <LocationSVG />
            <p className="text-base font-medium text-[#727E94]">
              {candidate.location}
            </p>
          </div>
        ) : null}
        <div className="flex items-center gap-1">
          <p className="text-base font-medium text-[#727E94]">
            Profile Visibility: {candidate.isPublic ? "Public" : "Private"}
          </p>
          {candidate.isPublic ? (
            <Eye stroke="#727E94" />
          ) : (
            <EyeOff stroke="#727E94" />
          )}
        </div>
      </div>
      <CandidateNameLocationTitleEditFormDialog profileId={profileId} />
    </div>
  );
};

const Biography = ({ profileId }: { profileId: string }) => {
  const { candidate, candidateLoading } = useCandidateQuery(profileId);

  const ref = useRef(null);
  const [readMore, setReadMore] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  const MAX_LINES = 6;
  const MAX_LINES_CLASS = "line-clamp-6";

  useLayoutEffect(() => {
    if (ref.current) {
      const { offsetHeight, scrollHeight } = ref.current;

      if (readMore) {
        // If readMore is true, check how many lines of text there are
        const lineHeight = parseFloat(getComputedStyle(ref.current).lineHeight);
        const lines = offsetHeight / lineHeight;

        // If there are more than MAX_LINES, then everything is fine
        if (lines > MAX_LINES) {
          return;
        } else {
          // If not, the text is not being truncated anyway and we can reset
          setIsTruncated(false);
          setReadMore(false);
        }
      } else if (offsetHeight && scrollHeight && offsetHeight < scrollHeight) {
        // If readMore is not true, then check if the text is being cut off by the line clamp
        setIsTruncated(true);
        setReadMore(false);
      } else {
        // In all other cases, reset
        setIsTruncated(false);
        setReadMore(false);
      }
    }
  }, [ref, readMore, candidate?.biography]);

  if (candidateLoading || !candidate) {
    return (
      <Skeleton className="card gap-3 w-full">
        <CardEditHeader text="Bio" />
        <SkeletonItem style={{ height: "128px", width: "100%" }} />
      </Skeleton>
    );
  }

  return (
    <div className="card gap-3 w-full">
      <CardEditHeader text="Bio">
        <CandidateBiographyEditFormDialog profileId={profileId} />
      </CardEditHeader>
      <div className="p-3 self-stretch bg-[#F2F5F5] rounded-xl">
        <p
          ref={ref}
          className={`text-sm font-medium text-[#4E4F6C] whitespace-break-spaces ${readMore ? "" : MAX_LINES_CLASS}`}
        >
          {candidate.biography}
        </p>
        {!isTruncated ? null : readMore ? (
          <p className="edit-text w-fit" onClick={() => setReadMore(false)}>
            View less
          </p>
        ) : (
          <p className="edit-text w-fit" onClick={() => setReadMore(true)}>
            View more
          </p>
        )}
      </div>
    </div>
  );
};

const IdealEngagement = ({ profileId }: { profileId: string }) => {
  const { candidate, candidateLoading } = useCandidateQuery(profileId);

  if (candidateLoading || !candidate) {
    return (
      <div className="card gap-3 w-full">
        <CardEditHeader text="Ideal engagement" />
        <div className="flex flex-col content-center items-start self-stretch">
          <ContentRow text="Role" response="None" loading />
          <ContentRow text="Type of Work" response="None" loading />
          <ContentRow text="Healthcare Area" response="None" loading />
          <ContentRow text="Specialty" response="None" loading />
          <ContentRow text="Company Headcount" response="None" isLast loading />
        </div>
      </div>
    );
  }

  return (
    <div className="card gap-3 w-full">
      <CardEditHeader text="Ideal engagement" isInternal>
        <CandidateIdealRoleEditFormDialog profileId={profileId} />
      </CardEditHeader>
      <div className="flex flex-col content-center items-start self-stretch">
        <ContentRow
          text="Role"
          response={candidate.idealRoles.join(", ") || "None"}
        />
        <ContentRow
          text="Type of Work"
          response={candidate.idealWorkType.join(", ") || "None"}
        />
        <ContentRow
          text="Healthcare Area"
          response={candidate.idealSubindustries.join(", ") || "None"}
        />
        <ContentRow
          text="Specialty"
          response={candidate.idealUseCase.join(", ") || "None"}
        />
        <ContentRow
          text="Company Headcount"
          response={candidate.idealCompanyHeadcount.join(", ") || "None"}
          isLast
        />
      </div>
    </div>
  );
};

const CoreCompetencies = ({ profileId }: { profileId: string }) => {
  const { candidate, candidateLoading } = useCandidateQuery(profileId);

  if (candidateLoading || !candidate) {
    return (
      <div className="card gap-3 w-full">
        <CardEditHeader text="Core Competencies" />
        <div className="flex flex-col content-center items-start self-stretch">
          <ContentRow text="Top Skills" response="None" loading />
          <ContentRow text="Secondary Skills" response="None" loading />
          <ContentRow text="Additional Skills" response="None" loading />
        </div>
      </div>
    );
  }

  return (
    <div className="card gap-3 w-full">
      <CardEditHeader text="Core Competencies" isInternal>
        <CandidateCompetenciesEditFormDialog profileId={profileId} />
      </CardEditHeader>
      <div className="flex flex-col content-center items-start self-stretch">
        <ContentRow
          text="Top Skills"
          response={candidate.topSkills.join(", ") || "None"}
        />
        <ContentRow
          text="Secondary Skills"
          response={candidate.secondarySkills.join(", ") || "None"}
        />
        <ContentRow
          text="Additional Skills"
          response={candidate.additionalSkills.join(", ") || "None"}
        />
      </div>
    </div>
  );
};

const Fundraising = ({ profileId }: { profileId: string }) => {
  const { candidate, candidateLoading } = useCandidateQuery(profileId);

  if (candidateLoading || !candidate) {
    return (
      <Skeleton className="card gap-3 w-full">
        <CardEditHeader text="Fundraising Experience" />
        <div className="flex flex-col content-center items-start self-stretch">
          <ContentRow text="Previous Fundraising Experience" loading isLast />
        </div>
      </Skeleton>
    );
  }

  return (
    <div className="card gap-3 w-full">
      <CardEditHeader text="Fundraising Experience">
        <CandidateFundraisingEditFormDialog profileId={profileId} />
      </CardEditHeader>
      <div className="flex flex-col content-center items-start self-stretch">
        <ContentRow
          text="Previous Fundraising Experience"
          responseIcon={
            candidate?.hasFundraisingExperience === undefined ? (
              <QuestionRegular height="24" width="24" />
            ) : candidate?.hasFundraisingExperience ? (
              <TickCircleSVG height="24" width="24" />
            ) : (
              <CloseCircleSVG height="24" width="24" />
            )
          }
        />
      </div>
    </div>
  );
};

const Availability = ({ profileId }: { profileId: string }) => {
  const { candidate, candidateLoading } = useCandidateQuery(profileId);

  if (candidateLoading || !candidate) {
    return (
      <Skeleton className="card gap-3 w-full">
        <CardEditHeader text="Availability" />
        <div className="flex flex-col content-center items-start self-stretch">
          <ContentRow
            Icon={<ClockSVG className="flex-shrink-0" height="24" width="24" />}
            text="Part-Time / Fractional Roles"
            loading
          />
          <ContentRow
            Icon={<UserSVG className="flex-shrink-0" height="24" width="24" />}
            text="Advisory Roles"
            loading
          />
          <ContentRow
            Icon={
              <PeopleSVG className="flex-shrink-0" height="24" width="24" />
            }
            text="Board Roles"
            loading
          />
          <ContentRow
            Icon={<ReceiptEditSVG className="flex-shrink-0" />}
            text="Full-Time Roles"
            loading
          />
          <ContentRow
            Icon={<CalendarSVG className="flex-shrink-0" />}
            text="Availability"
            response="Unknown"
            loading
          />
          <ContentRow
            Icon={<UserTickSVG className="flex-shrink-0" />}
            text="Available start date"
            response="Unknown"
            loading
            isLast
          />
        </div>
      </Skeleton>
    );
  }

  let startDateString;
  const startDate = dateStringToLocalDate(candidate?.availabilityStartDate);
  if (!startDate) {
    startDateString = "Unknown";
  } else if (startDate < new Date()) {
    startDateString = "Immediately";
  } else {
    startDateString = startDate.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  return (
    <div className="card gap-3 w-full">
      <CardEditHeader text="Availability">
        <CandidateAvailabilityEditFormDialog profileId={profileId} />
      </CardEditHeader>
      <div className="flex flex-col content-center items-start self-stretch">
        {/* <div className="flex flex-wrap gap-x-3 self-stretch items-center justify-between"> */}
        <ContentRow
          Icon={<ClockSVG className="flex-shrink-0" height="24" width="24" />}
          text="Part-Time / Fractional Roles"
          responseIcon={
            candidate.availabilityFractional ? (
              <TickCircleSVG height="24" width="24" />
            ) : (
              <CloseCircleSVG height="24" width="24" />
            )
          }
        />
        <ContentRow
          Icon={<UserSVG className="flex-shrink-0" height="24" width="24" />}
          text="Advisory Roles"
          responseIcon={
            candidate.availabilityAdvisory ? (
              <TickCircleSVG height="24" width="24" />
            ) : (
              <CloseCircleSVG height="24" width="24" />
            )
          }
        />
        <ContentRow
          Icon={<PeopleSVG className="flex-shrink-0" height="24" width="24" />}
          text="Board Roles"
          responseIcon={
            candidate.availabilityBoard ? (
              <TickCircleSVG height="24" width="24" />
            ) : (
              <CloseCircleSVG height="24" width="24" />
            )
          }
        />
        <ContentRow
          Icon={<ReceiptEditSVG className="flex-shrink-0" />}
          text="Full-Time Roles"
          responseIcon={
            candidate.availabilityFullTime ? (
              <TickCircleSVG height="24" width="24" />
            ) : (
              <CloseCircleSVG height="24" width="24" />
            )
          }
        />
        <ContentRow
          Icon={<CalendarSVG className="flex-shrink-0" />}
          text="Availability"
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          response={
            candidate.availabilityTime
              ? `${candidate.availabilityTime} hrs/week`
              : "Unknown"
          }
        />
        <ContentRow
          Icon={<UserTickSVG className="flex-shrink-0" />}
          text="Available start date"
          response={startDateString}
          isLast
        />
      </div>
    </div>
  );
};

const Pricing = ({ profileId }: { profileId: string }) => {
  const { candidate, candidateLoading } = useCandidateQuery(profileId);

  if (candidateLoading || !candidate) {
    return (
      <Skeleton className="card gap-3 w-full">
        <CardEditHeader text="Compensation" isInternal />
        <div className="flex flex-col content-center items-start self-stretch">
          <ContentRow
            Icon={<DollarCircleSVG className="flex-shrink-0" />}
            text="8 Hours per Week Monthly Retainer"
            response="Unknown"
            loading
          />
          <ContentRow
            Icon={<DollarCircleSVG className="flex-shrink-0" />}
            text="Hourly Rate"
            response="Unknown"
            isLast
            loading
          />
        </div>
      </Skeleton>
    );
  }

  return (
    <div className="card gap-3 w-full">
      <CardEditHeader text="Compensation" isInternal>
        <CandidatePricingEditFormDialog profileId={profileId} />
      </CardEditHeader>
      <div className="flex flex-col content-center items-start self-stretch">
        <ContentRow
          Icon={<DollarCircleSVG className="flex-shrink-0" />}
          text="8 Hours per Week Monthly Retainer"
          response={
            candidate.retainerDaily
              ? `${candidate.retainerDaily} USD`
              : "Unknown"
          }
        />
        <ContentRow
          Icon={<DollarCircleSVG className="flex-shrink-0" />}
          text="Hourly Rate"
          response={
            candidate.retainerHourly
              ? `${candidate.retainerHourly} USD`
              : "Unknown"
          }
          isLast
        />
      </div>
    </div>
  );
};

const Links = ({ profileId }: { profileId: string }) => {
  const { candidate, candidateLoading } = useCandidateQuery(profileId);

  if (candidateLoading || !candidate) {
    return (
      <Skeleton className="card gap-3 w-full">
        <CardEditHeader text="Links" />
        <div className="flex flex-col content-center items-start self-stretch">
          <div className="flex flex-wrap gap-x-3 self-stretch items-center justify-between border-bottom-line">
            <ContentRow
              Icon={
                <DocumentTextBoldSVG
                  className="flex-shrink-0"
                  height="20"
                  width="20"
                />
              }
              text="Resume"
              loading
              isLast
            />
            <ContentRow
              Icon={
                <SocialIconSVG
                  className="flex-shrink-0"
                  height="20"
                  width="20"
                />
              }
              text="Culture"
              loading
              isLast
            />
          </div>
          <div className="flex flex-wrap gap-x-3 self-stretch items-center justify-between">
            <ContentRow
              Icon={
                <MonitorMobileSVG
                  className="flex-shrink-0"
                  height="20"
                  width="20"
                />
              }
              text="Personal Website"
              loading
              isLast
            />
            <ContentRow
              Icon={
                <LinkedInSVG className="flex-shrink-0" height="20" width="20" />
              }
              text="Linkedin"
              loading
              isLast
            />
          </div>
        </div>
      </Skeleton>
    );
  }

  return (
    <div className="card gap-3 w-full">
      <CardEditHeader text="Links">
        <CandidateLinksEditFormDialog profileId={profileId} />
      </CardEditHeader>
      <div className="flex flex-col content-center items-start self-stretch">
        <div className="flex flex-wrap gap-x-3 self-stretch items-center justify-between border-bottom-line">
          <ContentRow
            Icon={
              <DocumentTextBoldSVG
                className="flex-shrink-0"
                height="20"
                width="20"
              />
            }
            text="Resume"
            responseIcon={
              candidate.resumeLink ? (
                <a
                  className="cursor-pointer"
                  href={candidate.resumeLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExportSVG height="20" width="20" stroke="#ED5C38" />
                </a>
              ) : (
                <ExportMissingSVG height="20" width="20" />
              )
            }
            isLast
          />
          <ContentRow
            Icon={
              <SocialIconSVG className="flex-shrink-0" height="20" width="20" />
            }
            text="Culture"
            responseIcon={
              candidate.cultureLink ? (
                <a
                  className="cursor-pointer"
                  href={candidate.cultureLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExportSVG height="20" width="20" stroke="#ED5C38" />
                </a>
              ) : (
                <ExportMissingSVG height="20" width="20" />
              )
            }
            isLast
          />
        </div>
        <div className="flex flex-wrap gap-x-3 self-stretch items-center justify-between">
          <ContentRow
            Icon={
              <MonitorMobileSVG
                className="flex-shrink-0"
                height="20"
                width="20"
              />
            }
            text="Personal Website"
            responseIcon={
              candidate.personalWebsite ? (
                <a
                  className="cursor-pointer"
                  href={candidate.personalWebsite}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExportSVG height="20" width="20" stroke="#ED5C38" />
                </a>
              ) : (
                <ExportMissingSVG height="20" width="20" />
              )
            }
            isLast
          />
          <ContentRow
            Icon={
              <LinkedInSVG className="flex-shrink-0" height="20" width="20" />
            }
            text="Linkedin"
            responseIcon={
              candidate.linkedIn ? (
                <a
                  className="cursor-pointer"
                  href={candidate.linkedIn}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExportSVG height="20" width="20" stroke="#ED5C38" />
                </a>
              ) : (
                <ExportMissingSVG height="20" width="20" />
              )
            }
            isLast
          />
        </div>
      </div>
    </div>
  );
};

const References = ({ profileId }: { profileId: string }) => {
  const { candidate } = useCandidateQuery(profileId);

  const sortedReferences = useMemo(
    () =>
      [...(candidate?.references ?? [])].sort((a, b) =>
        a.status === "Approved" && b.status !== "Approved"
          ? -1 // Order complete references first
          : b.status === "Approved" && a.status !== "Approved"
            ? 1
            : a.createdAt < b.createdAt // Otherwise, order most recent first
              ? 1
              : -1,
      ),
    [candidate?.references],
  );

  return (
    <div className="card gap-3 w-full">
      <CardEditHeader text="References" />
      {sortedReferences?.map((reference, idx) => (
        <Reference
          key={`reference-${reference.id ?? idx}`}
          profileId={profileId}
          {...reference}
          candidateName={candidate?.name}
          showControls
        />
      ))}
      <CandidateReferenceFormDialog profileId={profileId} />
    </div>
  );
};

const Projects = ({ profileId }: { profileId: string }) => {
  const { candidate } = useCandidateQuery(profileId);

  return (
    <div className="card gap-3 w-full">
      <CardEditHeader text="Project" />
      {candidate?.projects?.map((project, idx) => (
        <Project
          key={`project-${project.id ?? idx}`}
          project={project}
          projectId={project.id!}
          profileId={profileId}
          editable
        />
      ))}
      <CandidateProjectFormDialog profileId={profileId} />
    </div>
  );
};

const Experience = ({ profileId }: { profileId: string }) => {
  const { candidate, candidateLoading } = useCandidateQuery(profileId);

  const experiences = useMemo(() => candidate?.experiences ?? [], [candidate]);

  const [selectedExperienceId, setSelectedExperienceId] = useState("");
  const [hideSelected, setHideSelected] = useState(false);
  const [experienceFilter, setExperienceFilter] = useState("All");
  const selectedExperience = experiences.find(
    ({ id }) => id === selectedExperienceId,
  );

  const filteredExperiences =
    experienceFilter === "All"
      ? experiences
      : experiences.filter(
          ({ engagementType }) => engagementType === experienceFilter,
        );

  useEffect(() => {
    // If the selected experience is not one in the filtered list
    // Either because the filter has changed or no selection was made
    // Then set the first experience as the selected one
    if (!filteredExperiences.some(({ id }) => id === selectedExperienceId)) {
      setSelectedExperienceId(filteredExperiences[0]?.id ?? "");
      setHideSelected(true);
    }
  }, [experiences, filteredExperiences, selectedExperienceId]);

  const optionsSet: Set<string> = new Set<string>(["All"]);
  experiences.forEach(
    ({ engagementType }) => engagementType && optionsSet.add(engagementType),
  );

  if (candidateLoading || !candidate) {
    return (
      <div className="card justify-start max-h-[calc(95vh-73px)] w-full overflow-hidden">
        <div className="flex pb-4 gap-2 items-center self-stretch justify-between border-bottom-line">
          <div className="flex gap-2 items-center">
            <p className="text-lg font-semibold tracking-[-0.18px]">
              Experiences
            </p>
          </div>
        </div>
        <div className="flex-1 flex items-start self-stretch overflow-hidden">
          <div
            className={`flex flex-col w-[300px] py-4 pr-2 flex-grow md:flex-grow-0 items-start self-stretch bg-white md:border-right-line gap-3 overflow-auto ${!hideSelected ? "hidden md:flex" : ""}`}
          >
            <SkeletonItem style={{ height: "80px", width: "100%" }} />
            <SkeletonItem style={{ height: "80px", width: "100%" }} />
            <SkeletonItem style={{ height: "80px", width: "100%" }} />
          </div>
          <div className="flex flex-col py-4 pl-5 items-start gap-5 flex-1 h-full w-full">
            <SkeletonItem style={{ height: "100%", width: "100%" }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card justify-start max-h-[calc(95vh-73px)] w-full overflow-hidden">
      <div className="flex pb-4 gap-2 items-center self-stretch justify-between border-bottom-line">
        <div className="flex gap-2 items-center">
          <p className="text-lg font-semibold tracking-[-0.18px]">
            Experiences
          </p>
          {optionsSet.size > 2 ? (
            <Dropdown
              className="w-full md:w-auto"
              appearance="filled-darker"
              value={experienceFilter}
              onOptionSelect={(_evt, data) =>
                setExperienceFilter(data.selectedOptions[0]!)
              }
              style={{
                minWidth: "max-content",
                borderRadius: "999px",
              }}
            >
              {[...optionsSet].map((option) => (
                <FluentOption key={`experience-${option}`}>
                  {option}
                </FluentOption>
              ))}
            </Dropdown>
          ) : null}
        </div>
        <CandidateExperienceFormDialog profileId={profileId} />
      </div>
      <div className="flex-1 flex items-start self-stretch overflow-hidden">
        <div
          className={`flex flex-col w-[300px] py-4 pr-2 flex-grow md:flex-grow-0 items-start self-stretch bg-white md:border-right-line gap-3 overflow-auto ${!hideSelected ? "hidden md:flex" : ""}`}
        >
          {filteredExperiences.map(
            ({
              id,
              companyName,
              companyLogoLink,
              jobTitle,
              startDate,
              endDate,
              engagementType,
            }) => {
              const startString =
                dateStringToLocalDate(startDate)?.toLocaleDateString("en-US", {
                  month: "short",
                  year: "numeric",
                }) ?? "Unknown";
              const endString =
                dateStringToLocalDate(endDate)?.toLocaleDateString("en-US", {
                  month: "short",
                  year: "numeric",
                }) ?? "Present";
              const isSelected = id === selectedExperienceId;

              return (
                <div
                  key={`experience-${id}`}
                  className={`flex flex-col p-3 items-start self-stretch gap-3 rounded-xl cursor-pointer bg-white ${isSelected ? "md:bg-[#F3F5F7]" : ""} hover:bg-[#D1D9E1]`}
                  onClick={() => {
                    id
                      ? (setSelectedExperienceId(id), setHideSelected(false))
                      : null;
                  }}
                >
                  <div className="flex items-center self-stretch gap-3">
                    <Avatar
                      image={{ src: companyLogoLink }}
                      name={companyName}
                      size={48}
                    />
                    <div className="flex flex-col items-start gap-1">
                      <p className="text-sm font-semibold text-[#22234A]">{`${jobTitle} at ${companyName}`}</p>
                      <p className="text-sm text-[#727E94]">{`${startString} - ${endString}${engagementType ? ` (${engagementType})` : ""}`}</p>
                    </div>
                  </div>
                </div>
              );
            },
          )}
        </div>
        <ExperienceEntry
          profileId={profileId}
          experience={selectedExperience}
          hideOnMobile={hideSelected}
          closeOnMobileFn={() => setHideSelected(true)}
        />
      </div>
    </div>
  );
};

const ExperienceEntry = ({
  profileId,
  experience,
  hideOnMobile,
  closeOnMobileFn,
}: {
  profileId: string;
  experience?: IExperience;
  hideOnMobile: boolean;
  closeOnMobileFn: () => void;
}) => {
  if (!experience) {
    return <div className="flex flex-col py-4 pl-5 items-start gap-5 flex-1" />;
  }

  const selectedStartDateString =
    dateStringToLocalDate(experience.startDate)?.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    }) ?? "Unknown";
  const selectedEndDateString =
    dateStringToLocalDate(experience.endDate)?.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    }) ?? "Present";

  return (
    <div
      className={`${hideOnMobile ? "hidden md:flex" : "flex"} flex-col py-4 pl-5 self-stretch items-start gap-5 flex-1 overflow-auto`}
    >
      <div
        className="md:hidden min-h-7 min-w-7 cursor-pointer p-[2px] rounded-md hover:bg-[#d8d8d8]"
        onClick={closeOnMobileFn}
      >
        <ArrowLeftSVG />
      </div>
      <div className="flex items-center self-stretch gap-3">
        <Avatar
          image={{ src: experience.companyLogoLink }}
          name={experience.companyName}
          size={48}
        />
        <div className="flex flex-col gap-1 items-start self-stretch flex-1">
          <div className="flex self-stretch items-center justify-between">
            <p className="text-base font-semibold text-[#22234A]">{`${experience?.jobTitle} at ${experience?.companyName}`}</p>
            <CandidateExperienceFormDialog
              experience={experience}
              experienceId={experience.id!}
              profileId={profileId}
            />
          </div>
          <span className="text-sm font-medium text-[#727E94]">{`${selectedStartDateString} - ${selectedEndDateString}${experience?.engagementType ? ` (${experience.engagementType})` : ""}`}</span>
          {experience.location ? (
            <div className="flex gap-1 items-center">
              <LocationSVG />
              <p className="text-sm font-medium text-[#4E4F6C]">
                {experience.location}
              </p>
            </div>
          ) : null}
        </div>
      </div>
      {experience.functions?.length ? (
        <div className="flex flex-col gap-1 items-start self-stretch">
          <p className="text-sm font-semibold text-[#22234A]">
            {experience.functions.length === 1 ? "Function" : "Functions"}
          </p>
          <p className="text-sm font-medium text-[#4E4F6C] whitespace-break-spaces">
            {experience.functions.join(", ")}
          </p>
        </div>
      ) : null}
      {experience.companyDescription ? (
        <div className="flex flex-col gap-1 items-start self-stretch">
          <p className="text-sm font-semibold text-[#22234A]">
            Company Overview
          </p>
          <p className="text-sm font-medium text-[#4E4F6C] whitespace-break-spaces">
            {experience.companyDescription}
          </p>
        </div>
      ) : null}
      {(experience.industries ?? experience.otherIndustries) ? (
        <div className="flex flex-col gap-1 items-start self-stretch">
          <p className="text-sm font-semibold text-[#22234A]">Industry</p>
          <p className="text-sm font-medium text-[#4E4F6C] whitespace-break-spaces">
            {experience.industries ?? experience.otherIndustries}
          </p>
        </div>
      ) : null}
      {experience.stage ? (
        <div className="flex flex-col gap-1 items-start self-stretch">
          <p className="text-sm font-semibold text-[#22234A]">
            Stage when they joined
          </p>
          <p className="text-sm font-medium text-[#4E4F6C] whitespace-break-spaces">
            {experience.stage}
          </p>
        </div>
      ) : null}
      {experience.outcomes ? (
        <div className="flex flex-col gap-1 items-start self-stretch">
          <p className="text-sm font-semibold text-[#22234A]">
            Responsibilities and Outcomes
          </p>
          <p className="text-sm font-medium text-[#4E4F6C] whitespace-break-spaces">
            {experience.outcomes}
          </p>
        </div>
      ) : null}
    </div>
  );
};

const Questionnaires = ({ profileId }: { profileId: string }) => {
  const { candidate, candidateLoading } = useCandidateQuery(profileId);

  if (candidateLoading || !candidate) {
    return (
      <div className="card gap-4 items-stretch w-full">
        <CardEditHeader text="Questionnaires" />
        <QuestionnaireEntry
          question="Rank the top business principles that drive you"
          details="Place the tag sequentially"
          loading
        />
        <QuestionnaireEntry
          question="How would your peers describe you?"
          loading
        />
        <QuestionnaireEntry question="Communication Skills" loading />
      </div>
    );
  }

  return (
    <div className="card gap-4 items-stretch w-full">
      <CardEditHeader text="Questionnaires">
        <CandidateQuestionnaireEditFormDialog profileId={profileId} />
      </CardEditHeader>
      <QuestionnaireEntry
        question="Rank the top business principles that drive you"
        rankedList={candidate.questionnaireBusinessPrinciples}
      />
      <QuestionnaireEntry
        question="How would your peers describe you?"
        rankedList={candidate.questionnairePeerDescription}
      />
      <QuestionnaireEntry
        question="Communication Skills"
        rankedList={candidate.questionnaireCommunicationSkills}
      />
    </div>
  );
};

const Videos = ({ profileId }: { profileId: string }) => {
  const { candidate, candidateLoading } = useCandidateQuery(profileId);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | undefined>(
    undefined,
  );

  const dispatch = useAppDispatch();

  const onEditSuccess = (data: ProfileVideo[]) => {
    dispatch(replaceProfileVideos(data));
    setEditModalOpen(false);
  };

  if (!candidate || candidateLoading) {
    return (
      <div className="card gap-4 items-stretch w-full">
        <CardEditHeader text="About This Candidate" />
        <div className="flex flex-col self-stretch gap-3">
          <SkeletonItem style={{ height: "80px", width: "100%" }} />
          <SkeletonItem style={{ height: "80px", width: "100%" }} />
          <SkeletonItem style={{ height: "80px", width: "100%" }} />
        </div>
      </div>
    );
  }

  const filteredVideos =
    candidate.videos?.filter(({ showOnProfile }) => showOnProfile) ?? [];

  return (
    <div className="card gap-4 items-stretch w-full">
      <CardEditHeader
        text="About This Candidate"
        onClick={() => setEditModalOpen(true)}
      />
      <div className="flex flex-col self-stretch gap-3">
        {filteredVideos.length ? (
          filteredVideos.map((video) => (
            <VideoTile
              key={`video-${video.id}`}
              playVideoFn={() => setSelectedVideo(video.videoLink)}
              {...video}
            />
          ))
        ) : (
          <p className="text-base font-medium text-[#727E94]">
            You don&apos;t have any videos for your profile, consider adding one
            to introduce yourself.
            <br />
            <br />
            Videos give hiring teams a stronger sense of who you are beyond the
            resume.
          </p>
        )}
      </div>
      <VideoPlayer
        videoLink={selectedVideo}
        closeFn={() => setSelectedVideo(undefined)}
      />
      <CandidateProfileVideoDialog
        userId={profileId}
        videos={candidate.videos}
        open={editModalOpen}
        setOpen={setEditModalOpen}
        onSuccess={onEditSuccess}
        profileMode
      />
    </div>
  );
};

const VideoTile = ({
  title,
  summary,
  videoLink,
  playVideoFn,
}: {
  title?: string;
  summary?: string;
  videoLink: string;
  playVideoFn: () => void;
}) => {
  return (
    <div className="group p-3 max-h-40 hover:max-h-fit flex flex-row self-stretch gap-3 bg-[#fafafa] rounded-lg">
      <div className="relative flex-1 cursor-pointer" onClick={playVideoFn}>
        <video
          className="max-h-full w-full h-full object-cover"
          src={videoLink}
          controls={false}
          muted
          preload="metadata"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <div className="bg-white/80 rounded-full p-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-black"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M6.5 5.5v9l7-4.5-7-4.5z" />
            </svg>
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-col justify-between self-stretch gap-3">
        <div className="flex flex-col gap-1 self-stretch">
          <p className="line-clamp-2 group-hover:line-clamp-none overflow-hidden text-base font-semibold text-[#22234A] overflow-ellipsis">
            {title}
          </p>
          <p className="line-clamp-2 group-hover:line-clamp-none overflow-hidden text-sm text-[#727E94] overflow-ellipsis">
            {summary}
          </p>
        </div>
        <div
          className="flex flex-row gap-1 cursor-pointer"
          onClick={playVideoFn}
        >
          <CirclePlay stroke="#1A8376" height="20" width="20" />
          <p className="text-sm font-semibold text-[#1A8376]">Watch video</p>
        </div>
      </div>
    </div>
  );
};

const VideoPlayer = ({
  videoLink,
  closeFn,
}: {
  videoLink?: string;
  closeFn: () => void;
}) => {
  const dialogStyle = useDialogStyles();
  return (
    <Dialog
      open={Boolean(videoLink)}
      onOpenChange={(_evt, data) => !data.open && closeFn()}
    >
      <DialogSurface className={dialogStyle.editDialog}>
        <DialogBody className="pt-6">
          <DialogContent>
            <video
              className="w-full h-auto object-cover"
              src={videoLink}
              controls
              autoPlay
              preload="metadata"
            />
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

const QuestionnaireEntry = ({
  question,
  details,
  answer,
  rankedList,
  loading,
}: {
  question: string;
  details?: string;
  answer?: string;
  rankedList?: string[];
  loading?: boolean;
}) => {
  return (
    <div className="card p-3 gap-3 bg-[#003c320d]">
      <div className="flex flex-col gap-[2px] self-stretch items-start">
        <p className="text-sm font-semibold text-[#22234A]">{question}</p>
        <p className="text-sm font-medium text-[#4E4F6C]">{details}</p>
      </div>
      <div className="flex gap-[10px] p-3 self-stretch items-start bg-white rounded-xl">
        {loading ? (
          <Skeleton style={{ height: "80px", width: "100%" }}>
            <SkeletonItem style={{ height: "100%", width: "100%" }} />
          </Skeleton>
        ) : answer ? (
          <p className="text-sm font-medium text-[#4E4F6C]">{answer}</p>
        ) : (
          <div className="flex flex-col gap-1 items-start self-stretch">
            <ol className="list-[auto] pl-5">
              {rankedList?.map((skill, idx) => (
                <li
                  key={`skill-${idx}`}
                  className="text-sm font-medium text-[#22234A]"
                >
                  {skill}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

const CardEditHeader = ({
  text,
  isInternal,
  onClick,
  children,
}: {
  text: string;
  isInternal?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
}) => {
  return (
    <div className="flex self-stretch items-center justify-between pb-4 border-bottom-line">
      <div className="flex gap-4 self-stretch items-center">
        <p className="text-lg font-semibold tracking-[-0.18px] text-[#22234A]">
          {text}
        </p>
        {isInternal ? (
          <InfoLabel
            className="text-sm font-semibold tracking-[-0.18px] text-[#727E94]"
            size="large"
            info={
              <p>
                This information is used by Venturous to identify engagements
                you might be interested in. It is never shared with companies.
              </p>
            }
          >
            Private
          </InfoLabel>
        ) : null}
      </div>
      {onClick ? (
        <p className="edit-text" onClick={onClick}>
          Edit
        </p>
      ) : undefined}
      {children}
    </div>
  );
};

const ContentRow = ({
  Icon,
  text,
  response,
  responseIcon,
  isLast,
  loading,
}: {
  Icon?: JSX.Element;
  text: string;
  response?: string;
  responseIcon?: JSX.Element;
  isLast?: boolean;
  loading?: boolean;
}) => {
  return (
    <div
      className={`flex gap-1 self-stretch items-center justify-between min-h-12 py-4 ${isLast ? "" : "border-bottom-line"}`}
    >
      <div className="flex gap-3 items-center">
        {Icon}
        <p className="text-sm font-semibold text-[#22234A]">{text}</p>
      </div>
      {loading ? (
        response ? (
          <Skeleton style={{ height: "20px", width: "30%" }}>
            <SkeletonItem style={{ height: "100%", width: "100%" }} />
          </Skeleton>
        ) : (
          <Skeleton style={{ height: "24px", width: "24px" }}>
            <SkeletonItem
              shape="circle"
              style={{ height: "100%", width: "100%" }}
            />
          </Skeleton>
        )
      ) : (
        <>
          <p className="max-w-[70%] text-sm font-medium text-[#22234A] text-nowrap overflow-hidden overflow-ellipsis hover:overflow-visible hover:text-wrap">
            {response}
          </p>
          {responseIcon}
        </>
      )}
    </div>
  );
};

export default CandidateProfileEdit;
